import { openAuthModal } from "@blocks/_shared/auth/use-auth-modal";
import Button from "@blocks/_shared/button";
import { useEnrollment } from "@blocks/_shared/hooks/use-enrollment";
import { createRoot } from "@wordpress/element";
import { AnimatePresence, motion } from "motion/react";
/**
 * Referral Rewards Block - Frontend View
 *
 * Client-side rendering with animated expand of share options inside the link panel.
 */
import React, { useState } from "react";
import {
	type SocialPlatform,
	SocialShareGrid,
} from "~/shared/components/social-share";
import "./style.scss";

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	friendHeading: string;
	friendValue: string;
	friendDescription: string;
	yourHeading: string;
	yourValue: string;
	yourDescription: string;
	referralLinkLabel: string;
	buttonText: string;
	buttonTextJoin: string;
	buttonTextSignIn: string;
	shareMessage: string;
	cardLabelLevel: number;
	buttonTextColor?: string;
	buttonBackgroundColor?: string;
}

interface ReferralData {
	referralCode: string;
	referralUrl: string;
}

// ============================================================================
// Icons (from Figma)
// ============================================================================

const StarIcon = () => (
	<svg
		width="40"
		height="40"
		viewBox="0 0 40 40"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M19.2083 3.825C19.2814 3.67743 19.3942 3.55322 19.5341 3.46638C19.674 3.37953 19.8353 3.33351 20 3.33351C20.1647 3.33351 20.326 3.37953 20.4659 3.46638C20.6058 3.55322 20.7186 3.67743 20.7917 3.825L24.6417 11.6233C24.8953 12.1366 25.2697 12.5807 25.7327 12.9174C26.1957 13.2542 26.7335 13.4735 27.3 13.5567L35.91 14.8167C36.0731 14.8403 36.2264 14.9091 36.3525 15.0153C36.4786 15.1215 36.5724 15.2609 36.6234 15.4177C36.6743 15.5744 36.6805 15.7423 36.641 15.9024C36.6015 16.0624 36.518 16.2083 36.4 16.3233L30.1733 22.3867C29.7627 22.7868 29.4555 23.2808 29.2781 23.826C29.1007 24.3713 29.0584 24.9515 29.155 25.5167L30.625 34.0833C30.6538 34.2464 30.6362 34.4143 30.5742 34.5678C30.5121 34.7213 30.4082 34.8543 30.2742 34.9517C30.1403 35.049 29.9816 35.1067 29.8164 35.1182C29.6513 35.1297 29.4862 35.0945 29.34 35.0167L21.6433 30.97C21.1362 30.7037 20.572 30.5646 19.9992 30.5646C19.4264 30.5646 18.8621 30.7037 18.355 30.97L10.66 35.0167C10.5139 35.094 10.349 35.1288 10.1841 35.1171C10.0192 35.1054 9.86086 35.0476 9.72716 34.9504C9.59345 34.8531 9.48972 34.7203 9.42776 34.567C9.3658 34.4138 9.3481 34.2462 9.37667 34.0833L10.845 25.5183C10.942 24.9529 10.9 24.3723 10.7226 23.8267C10.5452 23.2812 10.2377 22.7869 9.82667 22.3867L3.6 16.325C3.48099 16.2101 3.39666 16.064 3.3566 15.9035C3.31655 15.7429 3.32239 15.5744 3.37346 15.417C3.42453 15.2596 3.51878 15.1197 3.64546 15.0133C3.77214 14.9069 3.92617 14.8382 4.09 14.815L12.6983 13.5567C13.2654 13.4742 13.804 13.2551 14.2676 12.9183C14.7313 12.5815 15.1062 12.1371 15.36 11.6233L19.2083 3.825Z" />
	</svg>
);

const TagIcon = () => (
	<svg
		width="40"
		height="40"
		viewBox="0 0 40 40"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M20.9767 4.31C20.3517 3.68484 19.504 3.33352 18.62 3.33333H6.66667C5.78261 3.33333 4.93477 3.68452 4.30964 4.30964C3.68452 4.93477 3.33333 5.78261 3.33333 6.66667V18.62C3.33352 19.504 3.68484 20.3517 4.31 20.9767L18.8167 35.4833C19.5742 36.2361 20.5987 36.6586 21.6667 36.6586C22.7346 36.6586 23.7591 36.2361 24.5167 35.4833L35.4833 24.5167C36.2361 23.7591 36.6586 22.7346 36.6586 21.6667C36.6586 20.5987 36.2361 19.5742 35.4833 18.8167L20.9767 4.31Z" />
		<path d="M13.1667 14.6667C13.9951 14.6667 14.6667 13.9951 14.6667 13.1667C14.6667 12.3382 13.9951 11.6667 13.1667 11.6667C12.3382 11.6667 11.6667 12.3382 11.6667 13.1667C11.6667 13.9951 12.3382 14.6667 13.1667 14.6667Z" />
	</svg>
);

const CopyIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
		<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
	</svg>
);

const CheckIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<polyline points="20 6 9 17 4 12" />
	</svg>
);

// ============================================================================
// Benefit Card
// ============================================================================

interface BenefitCardProps {
	icon: React.ReactNode;
	heading: string;
	value: string;
	description: string;
	labelTag: keyof JSX.IntrinsicElements;
}

const BenefitCard = ({
	icon,
	heading,
	value,
	description,
	labelTag: LabelTag,
}: BenefitCardProps) => (
	<div className="leat-referral-rewards__benefit">
		<div className="leat-referral-rewards__benefit-icon">{icon}</div>
		<div className="leat-referral-rewards__benefit-content">
			<LabelTag className="leat-referral-rewards__benefit-heading">
				<span className="leat-referral-rewards__benefit-heading-label">
					{heading}
				</span>
				<span className="leat-referral-rewards__benefit-heading-value">
					{value}
				</span>
			</LabelTag>
			<p className="leat-referral-rewards__benefit-description">
				{description}
			</p>
		</div>
	</div>
);

// ============================================================================
// Main Component
// ============================================================================

const ReferralRewardsView = ({ attributes }: { attributes: Attributes }) => {
	const { status } = useEnrollment();
	const [isExpanded, setIsExpanded] = useState(false);
	const [referralData, setReferralData] = useState<ReferralData | null>(null);
	const [copied, setCopied] = useState(false);

	React.useEffect(() => {
		if (window.leatReferralConfig) {
			setReferralData({
				referralCode: window.leatReferralConfig.referralCode || "",
				referralUrl: window.leatReferralConfig.homeUrl
					? `${window.leatReferralConfig.homeUrl}?ref=${window.leatReferralConfig.referralCode}`
					: "",
			});
		}
	}, []);

	const LabelTag =
		`h${attributes.cardLabelLevel}` as keyof JSX.IntrinsicElements;

	const buttonInlineStyles: React.CSSProperties = {};
	if (attributes.buttonTextColor) {
		buttonInlineStyles.color = attributes.buttonTextColor;
	}
	if (attributes.buttonBackgroundColor) {
		buttonInlineStyles.backgroundColor = attributes.buttonBackgroundColor;
	}

	const handleCopy = async () => {
		if (!referralData?.referralUrl) return;
		try {
			await navigator.clipboard.writeText(referralData.referralUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const handleSocialShare = (platform: SocialPlatform) => {
		console.log(`Shared on ${platform}`);
	};

	const benefits = (
		<div className="leat-referral-rewards__benefits">
			<BenefitCard
				icon={<StarIcon />}
				heading={attributes.friendHeading}
				value={attributes.friendValue}
				description={attributes.friendDescription}
				labelTag={LabelTag}
			/>
			<BenefitCard
				icon={<TagIcon />}
				heading={attributes.yourHeading}
				value={attributes.yourValue}
				description={attributes.yourDescription}
				labelTag={LabelTag}
			/>
		</div>
	);

	if (status === "guest") {
		return (
			<div className="leat-referral-rewards">
				{benefits}
				<div className="leat-referral-rewards__cta">
					<Button
						onClick={() => openAuthModal({ intent: "auto" })}
						variant="contained"
						className="leat-referral-rewards__pill leat-button--pill"
						style={buttonInlineStyles}
					>
						{attributes.buttonTextSignIn}
					</Button>
				</div>
			</div>
		);
	}

	if (status === "loggedInNotEnrolled") {
		return (
			<div className="leat-referral-rewards">
				{benefits}
				<div className="leat-referral-rewards__cta">
					<Button
						onClick={() => openAuthModal({ intent: "join" })}
						variant="contained"
						className="leat-referral-rewards__pill leat-button--pill"
						style={buttonInlineStyles}
					>
						{attributes.buttonTextJoin}
					</Button>
				</div>
			</div>
		);
	}

	if (status !== "enrolled") return null;

	const displayUrl = referralData?.referralUrl || "";

	return (
		<div className="leat-referral-rewards">
			{benefits}

			<div className="leat-referral-rewards__panel">
				<div className="leat-referral-rewards__panel-row">
					<div className="leat-referral-rewards__link-info">
						<span className="leat-referral-rewards__link-label">
							{attributes.referralLinkLabel}
						</span>
						<button
							type="button"
							className="leat-referral-rewards__link-value"
							onClick={handleCopy}
							title={copied ? "Copied!" : displayUrl}
							aria-label={copied ? "Copied!" : `Copy ${displayUrl}`}
						>
							<span className="leat-referral-rewards__link-text">
								{displayUrl}
							</span>
							<span
								className="leat-referral-rewards__link-icon"
								aria-hidden="true"
							>
								{copied ? <CheckIcon /> : <CopyIcon />}
							</span>
						</button>
					</div>

					<Button
						type="button"
						variant="contained"
						className="leat-referral-rewards__pill leat-referral-rewards__share-toggle leat-button--pill"
						onClick={() => setIsExpanded(!isExpanded)}
						aria-expanded={isExpanded}
						style={buttonInlineStyles}
					>
						{attributes.buttonText}
					</Button>
				</div>

				<AnimatePresence initial={false}>
					{isExpanded && displayUrl && (
						<motion.div
							className="leat-referral-rewards__share-row"
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.25, ease: "easeInOut" }}
						>
							<div className="leat-referral-rewards__share-row-inner">
								<SocialShareGrid
									url={displayUrl}
									message={attributes.shareMessage}
									onShare={handleSocialShare}
									className="leat-referral-rewards__share-grid"
								/>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	);
};

// ============================================================================
// Initialization
// ============================================================================

export function initReferralRewardsBlocks() {
	const blocks = document.querySelectorAll<HTMLElement>(
		".leat-referral-rewards",
	);

	for (const block of blocks) {
		const attributes: Attributes = {
			friendHeading: block.dataset.friendHeading || "Your friend gets",
			friendValue: block.dataset.friendValue || "75 points",
			friendDescription:
				block.dataset.friendDescription ||
				"Complete your first purchase to receive 75 points",
			yourHeading: block.dataset.yourHeading || "You get",
			yourValue: block.dataset.yourValue || "25% discount",
			yourDescription:
				block.dataset.yourDescription ||
				"Receive a 25% discount when your friends make their first purchase",
			referralLinkLabel:
				block.dataset.referralLinkLabel || "Your referral link",
			buttonText: block.dataset.buttonText || "Share with friends",
			buttonTextJoin: block.dataset.buttonTextJoin || "Join Program",
			buttonTextSignIn: block.dataset.buttonTextSignIn || "Sign In",
			shareMessage:
				block.dataset.shareMessage || "Check out this loyalty program!",
			cardLabelLevel: Number(block.dataset.cardLabelLevel) || 4,
			buttonTextColor: block.dataset.buttonTextColor || undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor || undefined,
		};

		createRoot(block).render(<ReferralRewardsView attributes={attributes} />);
	}
}

// Auto-initialize
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initReferralRewardsBlocks);
} else {
	initReferralRewardsBlocks();
}
